import type { DestroyOptions, Size, Texture } from 'pixi.js';
import { Assets, Container, Sprite, NineSliceSprite, Point } from 'pixi.js';
import { Signal } from 'typed-signals';
import { soundManager } from '../../../../app/SoundManager';

export interface LanguageSelectorViewConfig {
  readonly languageKeys: string[];
  readonly getActiveLanguage: () => string;
  readonly iconSize: Size;
  readonly iconPadding: number;
  readonly maxColumns: number;
}

interface LanguageIconMapEntry {
  languageKey: string;
  icon: Sprite;
}

export class LanguageSelectorView extends Container {
  private readonly selectedIcon = this.createSelectedIcon().props({
    parent: this,
  });

  private readonly panel = new Container().props({
    visible: false,
    alpha: 0,
    parent: this,
  });

  private readonly panelBg = new NineSliceSprite({
    texture: Assets.get('Language_Selection_Background'),
    topHeight: 32,
    bottomHeight: 32,
    leftWidth: 25,
    rightWidth: 25,
  }).props({
    x: -25,
    y: -20,
    parent: this.panel,
    height: 500,
  });

  private readonly panelIcons = new Container().props({
    parent: this.panel,
    y: 10,
  });

  private readonly panelSelectOverlay = Sprite.from('Selected_Language_Icon_Overlay').props({
    parent: this.panel,
    visible: false,
  });

  private readonly panelIconMap = new Map<string, LanguageIconMapEntry>();

  public readonly onLanguageSelected = new Signal<(languageKey: string) => void>();

  constructor(private readonly config: LanguageSelectorViewConfig) {
    super();

    this.addLanguageIcons(config.languageKeys);
    this.updateSelectPanelAlignment();
    this.selectLanguage(config.getActiveLanguage(), false);
  }

  private createSelectedIcon(): Sprite {
    const icon = Sprite.from('Locale_en_US').props({
      anchor: 0.5,
    });

    Sprite.from('Language_White_Frame').props({
      parent: icon,
      anchor: 0.5,
    });

    icon.interactive = true;
    icon.cursor = 'pointer';
    icon.onpointertap = (): void => {
      this.showPanel();
      soundManager.play('UI_Button_Click');
    };

    return icon;
  }

  public selectLanguage(languageKey: string, emitEvent = true): void {
    const { panelIconMap, panelSelectOverlay, selectedIcon } = this;
    const icon = panelIconMap.get(languageKey)?.icon;
    if (!icon) return;

    selectedIcon.texture = icon.texture;

    panelSelectOverlay.props({
      visible: true,
      x: icon.x + (icon.parent?.x ?? 0) - 1,
      y: icon.y + (icon.parent?.y ?? 0) - 1,
      width: icon.width + 2,
      height: icon.height + 2,
    });

    if (emitEvent) this.onLanguageSelected.emit(languageKey);
  }

  private addLanguageIcons(languageKeys: string[]): void {
    const { panelIconMap, panelIcons } = this;
    const { iconSize, iconPadding, maxColumns } = this.config;

    languageKeys.forEach((languageKey, i) => {
      const texture: Texture = Assets.get(`Locale_${languageKey}`);
      if (!texture) return;

      const col = i % maxColumns;
      const row = Math.floor(i / maxColumns);

      const icon = new Sprite(texture).props({
        width: iconSize.width,
        height: iconSize.height,
        x: col * iconSize.width + col * iconPadding,
        y: row * iconSize.height + row * iconPadding,
        parent: panelIcons,
      });

      icon.interactive = true;
      icon.cursor = 'pointer';
      icon.onpointertap = (): void => {
        this.selectLanguage(languageKey);
        void this.hidePanel();
        soundManager.play('UI_Button_Click');
      };

      panelIconMap.set(languageKey, { languageKey, icon });
    });
  }

  private showPanel(): void {
    const { panel } = this;

    const panelBounds = panel.getLocalBounds().rectangle.pad(40, 40);
    const localPos = new Point();

    panel.interactive = true;
    panel.onglobalpointermove = (e): void => {
      panel.toLocal(e.global, undefined, localPos);
      if (!panelBounds.contains(localPos.x, localPos.y)) {
        panel.onglobalpointermove = undefined;
        panel.interactive = false;
        void this.hidePanel();
      }
    };

    panel.visible = true;
    panel.removeTweens();
    panel.tweenTo({ alpha: 1 }, 100, 'quadOut', true);
  }

  public async hidePanel(delay = 400): Promise<void> {
    const { panel } = this;
    if (!panel.visible) return;

    this.interactiveChildren = false;
    panel.removeTweens();
    await panel.tweenTo({ alpha: 0 }, 100, 'quadOut', true, delay).asyncOnComplete();
    panel.visible = false;
    this.interactiveChildren = true;
  }

  private updateSelectPanelAlignment(): void {
    const { panel, panelBg, panelIcons } = this;
    const { iconSize } = this.config;

    panelBg.props({
      width: panelIcons.width + panelBg.leftWidth + panelBg.rightWidth,
      height: panelIcons.height + panelBg.topHeight + panelBg.bottomHeight,
    });

    panel.props({
      x: -(panel.width / 2) + panelBg.leftWidth,
      y: iconSize.height - panelBg.topHeight / 2,
    });
  }

  public destroy(options?: DestroyOptions): void {
    this.onLanguageSelected.disconnectAll();
    super.destroy(options);
  }
}
